import { Component, Output, EventEmitter, signal, computed, OnChanges, SimpleChanges, input, model } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { BottomSheetComponent } from "../../../../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { SELF_ASSIGNED_VENDOR, VendorDTO } from "../../../../../../../domains/vendors/models/VendorDTO";

@Component({
  selector: "app-vendor-selection-drawer",
  standalone: true,
  imports: [CommonModule, FormsModule, BottomSheetComponent],
  templateUrl: "./vendor-selection-drawer.component.html",
  styleUrls: ["./vendor-selection-drawer.component.scss"]
})
export class VendorSelectionDrawerComponent implements OnChanges {
  isOpen = model<boolean>(false);
  vendors = input<VendorDTO[]>([]);
  externalFinancialCommunityId = input.required<number>();

  initialSelectedVendorId = input<number | null>(null);
  isSelfAssigned = input<boolean>(false);
  assignedVendorIds = input<number[]>([]);
  isSaving = input<boolean>(false);
  @Output() selectionCanceled = new EventEmitter<boolean>();
  @Output() vendorSelected = new EventEmitter<VendorDTO>();

  searchTerm = signal<string>("");
  selectedVendorId = signal<number | null>(null);
  isAssignToMeSelected = signal<boolean>(false);

  filteredVendors = computed(() => {
    const term = this.searchTerm().toLowerCase();
    let vendorsToShow = [...this.vendors()];

    const vendorsToHide = this.assignedVendorIds().filter(id => id !== this.selectedVendorId()) || [];
    vendorsToHide.push(SELF_ASSIGNED_VENDOR.id);
    vendorsToShow = vendorsToShow.filter(vendor => vendorsToHide.includes(vendor.id) === false);

    if (term) {
      vendorsToShow = vendorsToShow.filter(vendor =>
        vendor.name.toLowerCase().includes(term) ||
        vendor.doingBusinessAs?.toLowerCase().includes(term)
      );
    }

    vendorsToShow.sort((a, b) => a.name.localeCompare(b.name));

    return vendorsToShow;
  });

  selectedVendor = computed(() => {
    const id = this.selectedVendorId();
    return id ? this.vendors().find(v => v.id === id) : null;
  });

  hasSelection = computed(() =>
    this.selectedVendorId() !== null || this.isAssignToMeSelected()
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["isOpen"] && this.isOpen() && !changes["isOpen"].previousValue) {
      if (this.isSelfAssigned()) {
        this.isAssignToMeSelected.set(true);
        this.selectedVendorId.set(null);
      } else if (this.initialSelectedVendorId()) {
        this.selectedVendorId.set(this.initialSelectedVendorId());
        this.isAssignToMeSelected.set(false);

        setTimeout(() => {
          this.scrollToKeepInView();
        }, 150);
      } else {
        this.selectedVendorId.set(null);
        this.isAssignToMeSelected.set(false);

        setTimeout(() => {
          const drawerContent = this.getDrawerContent();
          if (drawerContent) {
            drawerContent.scrollTo({ top: 0, behavior: "auto" });
          }
        }, 150);
      }
    }
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  onClearSearch(): void {
    this.searchTerm.set("");
  }

  onLeftButtonClick(): void {
    this.onCancel();
  }

  onRightButtonClick(): void {
    this.onSelect();
  }

  onVendorSelect(vendorId: number): void {
    this.selectedVendorId.set(vendorId);
    this.isAssignToMeSelected.set(false);

    setTimeout(() => {
      this.scrollToKeepInView();
    }, 100);
  }

  onAssignToMeSelect(): void {
    this.isAssignToMeSelected.set(true);
    this.selectedVendorId.set(null);
  }

  onCancel(): void {
    this.resetState();
    this.selectionCanceled.emit();
  }

  onSelect(): void {
    if (this.isAssignToMeSelected()) {
      this.vendorSelected.emit(SELF_ASSIGNED_VENDOR);
    } else {
      const vendor = this.selectedVendor();
      if (vendor) {
        this.vendorSelected.emit(vendor);
      }
    }

    this.resetState();
  }

  private resetState() {
    this.selectedVendorId.set(null);
    this.isAssignToMeSelected.set(false);
    this.searchTerm.set("");
  }

  private getDrawerContent(): Element | null {
    const currentDrawer = document.querySelector(".bottom-sheet.open");
    if (currentDrawer) {
      return currentDrawer.querySelector(".bottom-sheet-content");
    }
    return document.querySelector(".bottom-sheet-content");
  }

  private scrollToKeepInView(): void {
    setTimeout(() => {
      const selectedVendorId = this.selectedVendorId();
      if (!selectedVendorId) {
        return;
      }

      const drawerContent = this.getDrawerContent();
      if (!drawerContent) {
        return;
      }

      const selectedVendorElement = drawerContent.querySelector(
        `[data-testid="vendor-item-${selectedVendorId}"]`
      );

      if (selectedVendorElement) {
        selectedVendorElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest"
        });
      }
    }, 150);
  }
}
